import React from 'react';
import { TrendingUp, Clock, DollarSign, FileText, AlertTriangle, CheckCircle } from 'lucide-react';
import { formatCurrency } from '../utils/currencyUtils';
import type { ExchangeRates } from '../utils/currencyUtils';
import { ROITimelineChart } from './ROITimelineChart';
import { NumericFormat } from 'react-number-format';

interface ROIAnalysisProps {
  purchasePrice: number;
  monthlyProfit: number;
  writeoffMonths: number;
  breakEvenMonths: number;
  totalReturn: number;
  netProfit: number;
  totalROI: number;
  annualROI: number;
  currency: string;
  exchangeRates: ExchangeRates;
  onPurchasePriceChange: (price: number) => void;
  onWriteoffMonthsChange: (months: number) => void;
}

export function ROIAnalysis({
  purchasePrice,
  monthlyProfit,
  writeoffMonths,
  breakEvenMonths,
  totalReturn,
  netProfit,
  totalROI,
  annualROI,
  currency,
  exchangeRates,
  onPurchasePriceChange,
  onWriteoffMonthsChange
}: ROIAnalysisProps) {

  const getBreakEvenStatus = () => {
    if (!isFinite(breakEvenMonths)) return { text: 'Never', color: 'text-red-600', bg: 'bg-red-50' };
    if (breakEvenMonths <= 6) return { text: 'Fast', color: 'text-green-600', bg: 'bg-green-50' };
    if (breakEvenMonths <= 12) return { text: 'Moderate', color: 'text-yellow-600', bg: 'bg-yellow-50' };
    return { text: 'Slow', color: 'text-red-600', bg: 'bg-red-50' };
  };

  const getROIStatus = () => {
    if (!isFinite(totalROI) || totalROI <= 0) return { text: 'Negative', color: 'text-red-600', bg: 'bg-red-50' };
    if (totalROI >= 200) return { text: 'Exceptional', color: 'text-green-600', bg: 'bg-green-50' };
    if (totalROI >= 100) return { text: 'Good', color: 'text-yellow-600', bg: 'bg-yellow-50' };
    return { text: 'Moderate', color: 'text-blue-600', bg: 'bg-blue-50' };
  };

  const breakEvenStatus = getBreakEvenStatus();
  const roiStatus = getROIStatus();

  return (
    <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
      <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-blue-500" />
        Investment Analysis
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Purchase Price ({currency})
          </label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <NumericFormat
              value={purchasePrice}
              onValueChange={(values) => onPurchasePriceChange(values.floatValue || 0)}
              thousandSeparator
              decimalScale={2}
              prefix={currency === 'USD' ? '$' : ''}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter purchase price"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Write-off Period (Months)
          </label>
          <div className="relative">
            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <NumericFormat
              value={writeoffMonths}
              onValueChange={(values) => onWriteoffMonthsChange(values.floatValue || 24)}
              decimalScale={0}
              min={1}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter months"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Break-even Period */}
        <div className={`${breakEvenStatus.bg} rounded-xl p-6`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Break-even Period</h3>
            <Clock className={`w-5 h-5 ${breakEvenStatus.color}`} />
          </div>
          <div className="space-y-2">
            <p className={`text-3xl font-bold ${breakEvenStatus.color}`}>
              {!isFinite(breakEvenMonths) ? '∞' : breakEvenMonths.toFixed(1)}
            </p>
            <p className="text-sm text-gray-600">months</p>
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${breakEvenStatus.bg} ${breakEvenStatus.color}`}>
              {breakEvenStatus.text} Recovery
            </span>
          </div>
        </div>

        {/* Total Return */}
        <div className={`${roiStatus.bg} rounded-xl p-6`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Total Return</h3>
            <TrendingUp className={`w-5 h-5 ${roiStatus.color}`} />
          </div>
          <div className="space-y-2">
            <p className={`text-3xl font-bold ${roiStatus.color}`}>
              {isFinite(totalROI) ? totalROI.toFixed(1) : '0.0'}%
            </p>
            <p className="text-sm text-gray-600">total ROI</p>
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${roiStatus.bg} ${roiStatus.color}`}>
              {roiStatus.text} Performance
            </span>
          </div>
        </div>

        {/* Annual Yield */}
        <div className="bg-blue-50 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Annual Yield</h3>
            <DollarSign className="w-5 h-5 text-blue-600" />
          </div>
          <div className="space-y-2">
            <p className="text-3xl font-bold text-blue-600">
              {isFinite(annualROI) ? annualROI.toFixed(1) : '0.0'}%
            </p>
            <p className="text-sm text-gray-600">per year</p>
            <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-600">
              Effective Rate
            </span>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <ROITimelineChart
          purchasePrice={purchasePrice}
          monthlyProfit={monthlyProfit}
          writeoffMonths={writeoffMonths}
          currency={currency}
          exchangeRates={exchangeRates}
        />
      </div>

      <div className="mt-8 bg-gray-50 rounded-xl p-6 border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-gray-700" />
          <h3 className="text-lg font-semibold text-gray-900">Investment Analysis Summary</h3>
        </div>

        <div className="space-y-4">
          {/* Initial Investment */}
          <div className="flex items-start gap-4">
            <div className="w-full">
              <h4 className="text-sm font-medium text-gray-700 mb-1">Initial Investment</h4>
              <p className="text-gray-600">
                Initial capital requirement: {formatCurrency(purchasePrice, currency, exchangeRates)}
              </p>
            </div>
          </div>

          {/* Monthly Performance */}
          <div className="flex items-start gap-4">
            <div className="w-full">
              <h4 className="text-sm font-medium text-gray-700 mb-1">Monthly Performance</h4>
              <p className="text-gray-600">
                Expected monthly {monthlyProfit >= 0 ? 'profit' : 'loss'}: {' '}
                <span className={monthlyProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {formatCurrency(Math.abs(monthlyProfit), currency, exchangeRates)}
                </span>
              </p>
            </div>
          </div>

          {/* Break-even Analysis */}
          <div className="flex items-start gap-4">
            <div className="w-full">
              <h4 className="text-sm font-medium text-gray-700 mb-1">Break-even Analysis</h4>
              <p className="text-gray-600">
                {isFinite(breakEvenMonths) ? (
                  `Break-even point will be reached in ${breakEvenMonths.toFixed(1)} months ${
                    breakEvenMonths <= 12 
                      ? '(favorable)' 
                      : breakEvenMonths <= 24 
                        ? '(moderate)' 
                        : '(extended period)'
                  }`
                ) : (
                  'Break-even point will not be reached under current conditions'
                )}
              </p>
            </div>
          </div>

          {/* Total Returns */}
          <div className="flex items-start gap-4">
            <div className="w-full">
              <h4 className="text-sm font-medium text-gray-700 mb-1">Total Returns ({writeoffMonths} months)</h4>
              <div className="space-y-2">
                <p className="text-gray-600">
                  Total revenue: {formatCurrency(totalReturn, currency, exchangeRates)}
                </p>
                <p className="text-gray-600">
                  Net profit: {' '}
                  <span className={netProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {formatCurrency(netProfit, currency, exchangeRates)}
                  </span>
                </p>
                <p className="text-gray-600">
                  Return on Investment (ROI): {' '}
                  <span className={totalROI >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {totalROI.toFixed(1)}%
                  </span>
                </p>
                <p className="text-gray-600">
                  Annualized ROI: {' '}
                  <span className={annualROI >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {annualROI.toFixed(1)}%
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Risk Assessment */}
          <div className="flex items-start gap-4">
            <div className="w-full">
              <h4 className="text-sm font-medium text-gray-700 mb-1">Risk Assessment</h4>
              <div className="mt-2 flex items-center gap-2">
                {monthlyProfit > 0 ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                )}
                <p className="text-gray-600">
                  {monthlyProfit > 0 ? (
                    `Investment shows positive returns with ${annualROI.toFixed(1)}% annual ROI. ${
                      breakEvenMonths <= 12 
                        ? 'Quick break-even period indicates favorable risk-reward ratio.'
                        : 'Extended break-even period suggests moderate risk exposure.'
                    }`
                  ) : (
                    'Investment shows negative returns under current conditions. Consider adjusting parameters or exploring alternative options.'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {monthlyProfit <= 0 && (
        <div className="mt-6 p-4 bg-red-50 border border-red-100 rounded-xl">
          <p className="text-sm text-red-600">
            Warning: With current settings, this investment is not profitable. 
            Monthly loss: {formatCurrency(Math.abs(monthlyProfit), currency, exchangeRates)}
          </p>
        </div>
      )}
    </div>
  );
}